import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";
import EmptyState from "../../common/EmptyState";

const Billing = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const theme = useTheme();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    const res = await hitAxios({
      path: "/api/orders/my-orders",
      showSnackbar: false,
    });
    if (res?.data?.success) setOrders(res.data.data);
  };

  const statusColor = (s) =>
    ({ completed: "success", pending: "warning", failed: "error" })[s] ||
    "default";
  const methodColors = {
    paypal: "#0070BA",
    stripe: "#635BFF",
    offline: "#10B981",
  };

  return (
    <Box>
      <PageHeader
        title={lang.billingOrders || "Billing & Orders"}
        subtitle={`${orders.length} total orders`}
        icon={ReceiptIcon}
      />

      <Card>
        <CardContent sx={{ p: 0 }}>
          {orders.length === 0 ? (
            <EmptyState
              icon={ReceiptIcon}
              title={lang.noOrdersYet || "No orders yet"}
              description="Your billing history will appear here once you purchase a plan."
            />
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {[
                      "Order ID",
                      "Plan",
                      "Amount",
                      "Method",
                      "Status",
                      "Date",
                    ].map((h) => (
                      <TableCell key={h}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow
                      key={order.id}
                      sx={{
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                        },
                      }}
                    >
                      <TableCell>
                        <Typography
                          variant="caption"
                          fontFamily="monospace"
                          color="text.secondary"
                        >
                          #{order.uid?.substring(0, 10)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {order.plan_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="primary.main"
                        >
                          ${order.amount}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.payment_method || "N/A"}
                          size="small"
                          sx={{
                            bgcolor: alpha(
                              methodColors[order.payment_method] || "#888",
                              0.1,
                            ),
                            color: methodColors[order.payment_method] || "#888",
                            fontWeight: 700,
                            height: 22,
                            fontSize: "0.7rem",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.status}
                          size="small"
                          color={statusColor(order.status)}
                          sx={{
                            fontWeight: 600,
                            height: 22,
                            fontSize: "0.7rem",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Billing;
